import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { chatbotAdminApi } from '../../api/chatbotApi';
import { InfoFieldsEditor } from './InfoFieldsEditor';
import { padChatbotInfoFields, type ChatbotInfoField } from '@workspace/types';

type BotInfoFieldsQuickDialogProps = {
  botId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function BotInfoFieldsQuickDialog({
  botId,
  open,
  onOpenChange,
  onSaved,
}: BotInfoFieldsQuickDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<ChatbotInfoField[]>(padChatbotInfoFields([]));
  const [enabled, setEnabled] = useState(false);
  const [enableOnSave, setEnableOnSave] = useState(true);

  useEffect(() => {
    if (!open || !botId) {
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const detail = await chatbotAdminApi.getBotDetail(botId);
        setFields(padChatbotInfoFields(detail.bot.infoFields));
        const isEnabled = Boolean(detail.bot.botInfoFieldsEnabled);
        setEnabled(isEnabled);
        setEnableOnSave(!isEnabled);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Error al cargar info del bot';
        toast.error(message);
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [open, botId, onOpenChange]);

  const handleSave = async () => {
    if (!botId || saving) {
      return;
    }
    setSaving(true);
    try {
      await chatbotAdminApi.patchBotInfoFields(botId, {
        infoFields: fields,
        botInfoFieldsEnabled: enabled ? undefined : enableOnSave ? true : undefined,
      });
      toast.success('Aviso del bot actualizado. Entra en el próximo mensaje.');
      onSaved?.();
      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al guardar info del bot';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-xl'>
        <DialogHeader>
          <DialogTitle>Info / aviso del bot</DialogTitle>
          <DialogDescription>
            Esto lo ve el bot en todas las conversaciones. Sirve para avisos del día (corte,
            horario, sucursal).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className='text-sm text-muted-foreground'>Cargando...</p>
        ) : (
          <div className='space-y-4'>
            {!enabled && (
              <label className='flex items-start gap-2 text-sm'>
                <input
                  type='checkbox'
                  className='mt-1'
                  checked={enableOnSave}
                  onChange={event => setEnableOnSave(event.target.checked)}
                />
                <span>
                  Habilitar info del bot al guardar. Si queda apagada, el aviso se guarda pero no
                  se usa en las conversaciones.
                </span>
              </label>
            )}
            <InfoFieldsEditor idPrefix='bot-quick' fields={fields} onChange={setFields} />
          </div>
        )}
        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type='button' onClick={handleSave} disabled={loading || saving || !botId}>
            {saving ? 'Guardando...' : 'Guardar aviso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
